import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DeepNestedDialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BodySmall, BodyMedium, Caption } from "@/components/ui/typography";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/auth";
import { useUserDocuments } from "@/hooks/useUserDocuments";
import { Upload, FileText, Edit3, Check, FolderOpen, Award as AwardIcon, Shield, Calendar, Globe, Building2, GraduationCap, MapPin, Phone, Briefcase, Plus, Trash2, Linkedin } from "lucide-react";
import { InlineLoader } from '@/components/ui/loading';
import { useTranslation } from '@/hooks/useTranslation';
import { EnhancedAlert } from "@/components/ui/enhanced-alert";

// Error boundary component for handling runtime errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ResumeDialog Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryContent 
          onRetry={() => this.setState({ hasError: false, error: null })} 
        />
      );
    }

    return this.props.children;
  }
}

// Separate component to use hooks inside error boundary
const ErrorBoundaryContent = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation('ui');
  return (
    <div className="p-4 text-center">
      <BodySmall className="text-destructive">{t('import_resume.error_occurred')}</BodySmall>
      <Button 
        variant="outline" 
        onClick={onRetry}
        className="mt-2"
      >
        {t('import_resume.retry')}
      </Button>
    </div>
  );
};

interface ParsedResumeData {
  name: string;
  awards: Array<{
    date: string;
    description: string;
    issuer: string;
    title: string;
  }>;
  bio: string;
  certifications: Array<{
    date: string;
    description: string;
    issuer: string;
    name: string;
    url: string;
  }>;
  current_company: string;
  current_position: string;
  department: string;
  educations: Array<{
    activities: string;
    degree: string;
    description: string;
    end_date: string;
    field_of_study: string;
    grade: string;
    institution: string;
    is_current: boolean;
    start_date: string;
  }>;
  experiences: Array<{
    company: string;
    description: string;
    employment_type: string;
    end_date: string;
    is_current: boolean;
    location: string;
    position: string;
    start_date: string;
  }>;
  github_url: string;
  headline: string;
  job_title: string;
  linkedin_url: string;
  location: string;
  phone: string;
  projects: Array<{
    description: string;
    endDate: string;
    github_url: string;
    startDate: string;
    technologies: string[];
    title: string;
    url: string;
  }>;
  summary: string;
  twitter_url: string;
  website: string;
}

interface ImportResumeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type DialogStep = 'upload' | 'preview';
type EditingField = 'job_title' | 'bio' | 'location' | 'phone' | 'website' | 'linkedin_url' | null;

const ImportResumeDialog: React.FC<ImportResumeDialogProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('ui');
  const toast = useTranslatedToast();
  const { user } = useUser();
  const { uploadDocument, checkForDuplicates, uploadDocumentVersion } = useUserDocuments();

  const [step, setStep] = useState<DialogStep>('upload');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [parsedData, setParsedData] = useState<ParsedResumeData | null>(null);
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [tempEditData, setTempEditData] = useState<{[key: string]: any}>({});
  const [tempArrayEditData, setTempArrayEditData] = useState<{[key: string]: {[index: number]: {[field: string]: any}}}>({});
  const [isImporting, setIsImporting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    educations?: { index: number; fields: string[] }[];
    experiences?: { index: number; fields: string[] }[];
  }>({});

  const formatDateForDatabase = (dateString: string | null): string | null => {
    if (!dateString || dateString.trim() === '') return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError(t('import_resume.invalid_file_type') || "Please upload a PDF, DOC, DOCX, or TXT file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError(t('import_resume.file_too_large') || "File size must be less than 10MB");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setStep('upload');
      setProgress(25);

      const duplicates = await checkForDuplicates('resume.pdf');
      let uploadedDocument;

      if (duplicates.length > 0) {
        uploadedDocument = await uploadDocumentVersion(
          file,
          'version',
          undefined,
          (progress) => setProgress(25 + (progress * 0.25))
        );
      } else {
        uploadedDocument = await uploadDocument(
          file,
          (progress) => setProgress(25 + (progress * 0.25))
        );
      }

      if (!uploadedDocument) {
        throw new Error('Failed to upload resume file');
      }

      setProgress(50);

      setStep('upload');

      const urlParts = uploadedDocument.file_url.split('/user-documents/');
      const storagePath = urlParts[1];
      const fullStorageKey = `user-documents/${storagePath}`;

      const { data: parseResponse, error: parseError } = await supabase.functions.invoke('evee-ai-resume-parse', {
        body: {
          key: fullStorageKey,
          userId: user?.id
        }
      });

      if (parseError || !parseResponse?.success) {
        console.error('Parse error:', parseError || parseResponse?.error);
        throw new Error(parseResponse?.error || t('import_resume.parse_failed') || 'Failed to parse resume. Please try again.');
      }

      const parseResult = parseResponse.data;

      const apiData = parseResult;

      const transformedData: ParsedResumeData = {
        name: apiData.displayName || apiData.name || "",
        awards: (apiData.awards || []).map(award => ({
          title: award.title || '',
          issuer: award.issuer || '',
          date: award.date || '',
          description: award.description || ''
        })),
        bio: apiData.bio || '',
        certifications: (apiData.certifications || []).map(cert => ({
          name: cert.name || '',
          issuer: cert.issuer || '',
          date: cert.date || '',
          description: cert.description || '',
          url: cert.url || ''
        })),
        current_company: apiData.currentCompany || '',
        current_position: apiData.currentPosition || '',
        department: apiData.department || '',
        educations: (apiData.educations || apiData.education || []).map(edu => ({
          degree: edu.degree || '',
          institution: edu.institution || '',
          field_of_study: edu.fieldOfStudy || edu.field_of_study || '',
          start_date: edu.startDate || '',
          end_date: edu.endDate || '',
          is_current: edu.isCurrent || false,
          grade: edu.grade || '',
          activities: edu.activities || '',
          description: edu.description || ''
        })),
        experiences: (apiData.experiences || []).map(exp => ({
          company: exp.company || '',
          position: exp.position || '',
          location: exp.location || '',
          employment_type: exp.employmentType || '',
          start_date: exp.startDate || '',
          end_date: exp.endDate || '',
          is_current: exp.isCurrent || false,
          description: exp.description || ''
        })),
        github_url: apiData.githubUrl || '',
        headline: apiData.headline || '',
        job_title: apiData.jobTitle || '',
        linkedin_url: apiData.linkedinUrl || '',
        location: apiData.location || '',
        phone: apiData.phone || '',
        projects: (apiData.projects || []).map(project => ({
          title: project.title || '',
          description: project.description || '',
          startDate: project.startDate || '',
          endDate: project.endDate || '',
          url: project.url || '',
          github_url: project.githubUrl || '',
          technologies: project.technologies || []
        })),
        summary: apiData.summary || '',
        twitter_url: apiData.twitterUrl || '',
        website: apiData.website || ''
      };

      setParsedData(transformedData);
      setProgress(100);
      setStep('preview');
    } catch (error) {
      console.error('Error processing resume:', error);
      setError(error instanceof Error ? error.message : t('import_resume.processing_failed') || "Failed to process resume");
      toast.raw.error(t('import_resume.processing_failed') || "Failed to process resume. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFieldEdit = (field: string, value: any) => {
    if (!parsedData) return;
    setTempEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldSave = (field: string) => {
    if (!parsedData) return;
    if (tempEditData[field] !== undefined) {
      setParsedData({ ...parsedData, [field]: tempEditData[field] });
      setTempEditData(prev => {
        const newData = { ...prev };
        delete newData[field];
        return newData;
      });
    }
    setEditingField(null);
  };

  const handleFieldCancel = () => {
    setTempEditData(prev => {
      const newData = { ...prev };
      if (editingField) {
        delete newData[editingField];
      }
      return newData;
    });
    setEditingField(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFieldSave(field);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleFieldCancel();
    }
  };

  const handleArrayItemEdit = (arrayField: string, index: number, itemField: string, value: any) => {
    if (!parsedData) return;
    const displayItems = getDisplayItems(arrayField);
    if (index >= displayItems.length) return;
    const originalArray = (parsedData as any)[arrayField];
    const tempData = tempArrayEditData[arrayField];
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    const targetIndex = index >= originalArray.length ? index : originalIndex;
    setTempArrayEditData(prev => ({
      ...prev,
      [arrayField]: {
        ...prev[arrayField],
        [targetIndex]: {
          ...prev[arrayField]?.[targetIndex],
          [itemField]: value
        }
      }
    }));
    
    // Re-validate after edit
    setTimeout(() => validateImportData(), 0);
  };

  const applyArrayEdits = (arrayField: string) => {
    if (!parsedData || !tempArrayEditData[arrayField]) return;
    const array = [...(parsedData as any)[arrayField]];
    const tempData = tempArrayEditData[arrayField];
    const itemsToRemove: number[] = [];
    Object.entries(tempData).forEach(([indexStr, fields]) => {
      const index = parseInt(indexStr);
      if (fields.__deleted) {
        itemsToRemove.push(index);
      }
    });
    const filteredArray = array.filter((_, index) => !itemsToRemove.includes(index));
    Object.entries(tempData).forEach(([indexStr, fields]) => {
      const index = parseInt(indexStr);
      if (fields.__deleted) return;
      if (index >= 0 && index < filteredArray.length) {
        filteredArray[index] = { ...filteredArray[index], ...fields };
      } else if (index >= filteredArray.length) {
        const newItem = { ...fields };
        delete newItem.__deleted;
        filteredArray.push(newItem);
      }
    });
    setParsedData({ ...parsedData, [arrayField]: filteredArray });
    setTempArrayEditData(prev => {
      const newData = { ...prev };
      delete newData[arrayField];
      return newData;
    });
  };

  const handleArrayItemAdd = (arrayField: string, newItem: any) => {
    if (!parsedData) return;
    const array = [...(parsedData as any)[arrayField], newItem];
    setParsedData({ ...parsedData, [arrayField]: array });
  };

  const handleArrayItemRemove = (arrayField: string, index: number) => {
    if (!parsedData) return;
    const array = (parsedData as any)[arrayField].filter((_: any, i: number) => i !== index);
    setParsedData({ ...parsedData, [arrayField]: array });
  };

  const addExperience = () => {
    if (!parsedData) return;
    const newExp = {
      position: "",
      company: "",
      location: "",
      employment_type: "",
      start_date: "",
      end_date: "",
      is_current: false,
      description: ""
    };
    setTempArrayEditData(prev => ({
      ...prev,
      experiences: {
        ...prev.experiences,
        [parsedData.experiences.length]: newExp
      }
    }));
  };

  const removeExperience = (index: number) => {
    if (!parsedData) return;
    const displayItems = getDisplayItems('experiences');
    if (index >= displayItems.length) return;
    const originalArray = parsedData.experiences;
    const tempData = tempArrayEditData.experiences;
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    setTempArrayEditData(prev => ({
      ...prev,
      experiences: {
        ...prev.experiences,
        [originalIndex]: { __deleted: true }
      }
    }));
  };

  const addEducation = () => {
    if (!parsedData) return;
    const newEdu = {
      degree: "",
      institution: "",
      field_of_study: "",
      grade: "",
      start_date: "",
      end_date: "",
      is_current: false,
      activities: "",
      description: ""
    };
    setTempArrayEditData(prev => ({
      ...prev,
      educations: {
        ...prev.educations,
        [parsedData.educations.length]: newEdu
      }
    }));
  };

  const removeEducation = (index: number) => {
    if (!parsedData) return;
    const displayItems = getDisplayItems('educations');
    if (index >= displayItems.length) return;
    const originalArray = parsedData.educations;
    const tempData = tempArrayEditData.educations;
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    setTempArrayEditData(prev => ({
      ...prev,
      educations: {
        ...prev.educations,
        [originalIndex]: { __deleted: true }
      }
    }));
  };

  const addProject = () => {
    if (!parsedData) return;
    const newProject = {
      title: "",
      description: "",
      technologies: [] as string[],
      url: "",
      github_url: "",
      startDate: "",
      endDate: ""
    };
    setTempArrayEditData(prev => ({
      ...prev,
      projects: {
        ...prev.projects,
        [parsedData.projects.length]: newProject
      }
    }));
  };

  const removeProject = (index: number) => {
    if (!parsedData) return;
    const displayItems = getDisplayItems('projects');
    if (index >= displayItems.length) return;
    const originalArray = parsedData.projects;
    const tempData = tempArrayEditData.projects;
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    setTempArrayEditData(prev => ({
      ...prev,
      projects: {
        ...prev.projects,
        [originalIndex]: { __deleted: true }
      }
    }));
  };

  const addCertification = () => {
    if (!parsedData) return;
    const newCert = {
      name: "",
      issuer: "",
      date: "",
      url: "",
      description: ""
    };
    setTempArrayEditData(prev => ({
      ...prev,
      certifications: {
        ...prev.certifications,
        [parsedData.certifications.length]: newCert
      }
    }));
  };

  const removeCertification = (index: number) => {
    if (!parsedData) return;
    const displayItems = getDisplayItems('certifications');
    if (index >= displayItems.length) return;
    const originalArray = parsedData.certifications;
    const tempData = tempArrayEditData.certifications;
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    setTempArrayEditData(prev => ({
      ...prev,
      certifications: {
        ...prev.certifications,
        [originalIndex]: { __deleted: true }
      }
    }));
  };

  const addAward = () => {
    if (!parsedData) return;
    const newAward = {
      title: "",
      issuer: "",
      date: "",
      description: ""
    };
    setTempArrayEditData(prev => ({
      ...prev,
      awards: {
        ...prev.awards,
        [parsedData.awards.length]: newAward
      }
    }));
  };

  const removeAward = (index: number) => {
    if (!parsedData) return;
    const displayItems = getDisplayItems('awards');
    if (index >= displayItems.length) return;
    const originalArray = parsedData.awards;
    const tempData = tempArrayEditData.awards;
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    setTempArrayEditData(prev => ({
      ...prev,
      awards: {
        ...prev.awards,
        [originalIndex]: { __deleted: true }
      }
    }));
  };

  const getTempArrayValue = (arrayField: string, index: number, field: string, originalValue: any) => {
    const displayItems = getDisplayItems(arrayField);
    if (index >= displayItems.length) return originalValue;
    const displayItem = displayItems[index];
    if (!displayItem) return originalValue;
    const originalArray = (parsedData as any)[arrayField];
    const tempData = tempArrayEditData[arrayField];
    let originalIndex = index;
    let currentIndex = 0;
    for (let i = 0; i < originalArray.length; i++) {
      if (tempData?.[i]?.__deleted) {
        continue;
      }
      if (currentIndex === index) {
        originalIndex = i;
        break;
      }
      currentIndex++;
    }
    if (index >= originalArray.length) {
      return displayItem[field] || originalValue;
    }
    const tempItem = tempData?.[originalIndex];
    if (tempItem?.__deleted) {
      return undefined;
    }
    const tempFieldValue = tempItem?.[field];
    return tempFieldValue !== undefined ? tempFieldValue : displayItem[field] || originalValue;
  };

  const toggleSectionEdit = (section: string) => {
    setEditingField(null);
    setEditingSections(prev => {
      const newState = { ...prev };
      if (newState[section]) {
        applyArrayEdits(section);
      }
      newState[section] = !newState[section];
      return newState;
    });
  };

  const [editingSections, setEditingSections] = useState<{[key: string]: boolean}>({});

  const getDisplayItems = (arrayField: string) => {
    if (!parsedData) return [];
    const originalArray = (parsedData as any)[arrayField];
    const tempData = tempArrayEditData[arrayField];
    if (!tempData) return originalArray;
    const result = [];
    const maxIndex = Math.max(originalArray.length - 1, ...Object.keys(tempData).map(k => parseInt(k)));
    for (let i = 0; i <= maxIndex; i++) {
      const tempItem = tempData[i];
      if (tempItem?.__deleted) {
        continue;
      }
      if (i < originalArray.length) {
        const originalItem = originalArray[i];
        const modifiedItem = tempItem ? { ...originalItem, ...tempItem } : originalItem;
        result.push(modifiedItem);
      } else if (tempItem) {
        const newItem = { ...tempItem };
        delete newItem.__deleted;
        result.push(newItem);
      }
    }
    return result;
  };

  const ContentComponent = DialogContent;

  const validateImportData = (): boolean => {
    const errors: typeof validationErrors = {};
    
    // Validate educations - use merged data
    const educationArray = parsedData?.educations || (parsedData as any)?.education || [];
    const educationErrors: { index: number; fields: string[] }[] = [];
    
    educationArray.forEach((edu: any, index: number) => {
      const missingFields: string[] = [];
      
      // Get the merged value (temp edits override original)
      const institution = getTempArrayValue('educations', index, 'institution', edu.institution);
      const degree = getTempArrayValue('educations', index, 'degree', edu.degree);
      
      if (!institution || institution.trim() === '') {
        missingFields.push('institution');
      }
      if (!degree || degree.trim() === '') {
        missingFields.push('degree');
      }
      
      if (missingFields.length > 0) {
        educationErrors.push({ index, fields: missingFields });
      }
    });
    
    if (educationErrors.length > 0) {
      errors.educations = educationErrors;
    }
    
    // Validate experiences - use merged data
    const experienceErrors: { index: number; fields: string[] }[] = [];
    (parsedData?.experiences || []).forEach((exp: any, index: number) => {
      const missingFields: string[] = [];
      
      // Get the merged value (temp edits override original)
      const position = getTempArrayValue('experiences', index, 'position', exp.position);
      const company = getTempArrayValue('experiences', index, 'company', exp.company);
      
      if (!position || position.trim() === '') {
        missingFields.push('position');
      }
      if (!company || company.trim() === '') {
        missingFields.push('company');
      }
      
      if (missingFields.length > 0) {
        experienceErrors.push({ index, fields: missingFields });
      }
    });
    
    if (experienceErrors.length > 0) {
      errors.experiences = experienceErrors;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleImportToProfile = async () => {
    if (!parsedData || !user?.id) return;

    // Validate before import
    if (!validateImportData()) {
      toast.error(t('import_resume.validation_error'));
      return;
    }

    setIsImporting(true);
    try {
      const userId = user.id;

      const profileUpdate = {
        bio: parsedData.bio || undefined,
        job_title: parsedData.job_title || undefined,
        current_company: parsedData.current_company || undefined,
        current_position: parsedData.current_position || undefined,
        summary: parsedData.summary || undefined,
        headline: parsedData.headline || undefined,
        phone: parsedData.phone || undefined,
        location: parsedData.location || undefined,
        website: parsedData.website || undefined,
        linkedin_url: parsedData.linkedin_url || undefined,
        github_url: parsedData.github_url || undefined,
        twitter_url: parsedData.twitter_url || undefined,
        department: parsedData.department || undefined,
      };

      const cleanedUpdate = Object.fromEntries(
        Object.entries(profileUpdate).filter(([_, value]) => value !== undefined && value !== '')
      );

      if (Object.keys(cleanedUpdate).length > 0) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update(cleanedUpdate as any)
          .eq('user_id', userId as any);

        if (profileError) throw profileError;
      }

      if (parsedData.projects && parsedData.projects.length > 0) {
        await supabase.from('user_projects').delete().eq('user_id', userId as any);
        const projectsToSave = parsedData.projects
          .filter(project => project.title)
          .map(project => ({
            user_id: userId,
            title: project.title,
            description: project.description || '',
            start_date: formatDateForDatabase(project.startDate),
            end_date: formatDateForDatabase(project.endDate),
            url: project.url || '',
            github_url: project.github_url || '',
            technologies: project.technologies || []
          }));

        if (projectsToSave.length > 0) {
          const { error: projectError } = await supabase
            .from('user_projects')
            .insert(projectsToSave as any);
          if (projectError) throw projectError;
        }
      } else {
        await supabase.from('user_projects').delete().eq('user_id', userId as any);
      }

      await supabase.from('user_certifications').delete().eq('user_id', userId as any);
      if (parsedData.certifications && parsedData.certifications.length > 0) {
        const certToSave = parsedData.certifications
          .filter(cert => cert.name)
          .map(cert => ({
            user_id: userId,
            name: cert.name,
            issuer: cert.issuer || '',
            date: formatDateForDatabase(cert.date),
            description: cert.description || '',
            url: cert.url || ''
          }));

        if (certToSave.length > 0) {
          const { error: certError } = await supabase
            .from('user_certifications')
            .insert(certToSave as any);
          if (certError) throw certError;
        }
      }

      await supabase.from('user_awards').delete().eq('user_id', userId as any);
      if (parsedData.awards && parsedData.awards.length > 0) {
        const awardsToSave = parsedData.awards
          .filter(award => award.title)
          .map(award => ({
            user_id: userId,
            title: award.title,
            issuer: award.issuer || '',
            date: formatDateForDatabase(award.date),
            description: award.description || ''
          }));

        if (awardsToSave.length > 0) {
          const { error: awardError } = await supabase
            .from('user_awards')
            .insert(awardsToSave as any);
          if (awardError) throw awardError;
        }
      }

      if (parsedData.experiences && parsedData.experiences.length > 0) {
        await supabase.from('user_experiences').delete().eq('user_id', userId as any);
        const expToSave = parsedData.experiences
          .filter(exp => exp.position && exp.company)
          .map(exp => ({
            user_id: userId,
            position: exp.position,
            company: exp.company,
            location: exp.location || '',
            employment_type: exp.employment_type || '',
            start_date: formatDateForDatabase(exp.start_date),
            end_date: formatDateForDatabase(exp.end_date),
            is_current: exp.is_current || false,
            description: exp.description || ''
          }));

        if (expToSave.length > 0) {
          const { error: expError } = await supabase
            .from('user_experiences')
            .insert(expToSave as any);
          if (expError) throw expError;
        }
      } else {
        await supabase.from('user_experiences').delete().eq('user_id', userId as any);
      }

      // Handle both 'educations' and 'education' field names
      console.log('Education data check:', {
        hasEducations: !!parsedData.educations,
        educationsLength: parsedData.educations?.length || 0,
        hasEducation: !!(parsedData as any).education,
        educationLength: (parsedData as any).education?.length || 0,
        sample: parsedData.educations?.[0] || (parsedData as any).education?.[0]
      });

      const educationArray = parsedData.educations || (parsedData as any).education || [];

      if (educationArray.length > 0) {
        await supabase.from('user_educations').delete().eq('user_id', userId as any);
        
        const eduToSave = educationArray.map((edu: any) => ({
            user_id: userId,
            degree: edu.degree,
            institution: edu.institution,
            field_of_study: edu.field_of_study || '',
            grade: edu.grade || '',
            start_date: formatDateForDatabase(edu.start_date),
            end_date: formatDateForDatabase(edu.end_date),
            is_current: edu.is_current || false,
            activities: edu.activities || '',
            description: edu.description || ''
          }));

        if (eduToSave.length > 0) {
          const { error: eduError } = await supabase
            .from('user_educations')
            .insert(eduToSave as any);
          
          if (eduError) throw eduError;
        }
      } else {
        await supabase.from('user_educations').delete().eq('user_id', userId as any);
      }

      toast.success(t('import_resume.profile_updated_success'));

      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(t('import_resume.profile_update_failed'));
    } finally {
      setIsImporting(false);
    }
  };

  const handleBackToUpload = () => {
    setStep('upload');
    setParsedData(null);
    setEditingField(null);
    setEditingSections({});
    setTempEditData({});
    setTempArrayEditData({});
    setProgress(0);
    setError("");
  };

  if (step === 'preview' && parsedData) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <ContentComponent className="sm:max-w-[90vw] max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t('import_resume.review_title')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {t('import_resume.review_description')}
            </p>

            {/* Basic Information */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">{t('import_resume.basic_information')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('import_resume.name')}</label>
                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded border">
                      <span className="font-medium">{parsedData.name || t('import_resume.name_not_available')}</span>
                      <div className="text-xs text-muted-foreground">{t('import_resume.auto_filled')}</div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('import_resume.job_title')}</label>
                    {editingField === 'job_title' ? (
                      <div className="space-y-2">
                        <Input
                          value={tempEditData.job_title !== undefined ? tempEditData.job_title : parsedData.job_title}
                          onChange={(e) => handleFieldEdit('job_title', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, 'job_title')}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleFieldSave('job_title')}>
                            <Check className="w-4 h-4 mr-1" />
                            {t('import_resume.save')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleFieldCancel}>
                            {t('import_resume.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-muted/20 rounded border">
                        <span className="font-medium">{parsedData.job_title}</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingField('job_title')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="text-sm font-medium mb-2 block">{t('import_resume.bio')}</label>
                  {editingField === 'bio' ? (
                    <div className="space-y-2">
                      <Textarea
                        value={tempEditData.bio !== undefined ? tempEditData.bio : parsedData.bio}
                        onChange={(e) => handleFieldEdit('bio', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'bio')}
                        autoFocus
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleFieldSave('bio')}>
                          <Check className="w-4 h-4 mr-1" />
                          {t('import_resume.save')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleFieldCancel}>
                          {t('import_resume.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between p-3 bg-muted/20 rounded border">
                      <span className="text-sm leading-relaxed flex-1">{parsedData.bio}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingField('bio')}
                        className="ml-2 flex-shrink-0"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('import_resume.location')}</label>
                    {editingField === 'location' ? (
                      <div className="space-y-2">
                        <Input
                          value={tempEditData.location !== undefined ? tempEditData.location : parsedData.location}
                          onChange={(e) => handleFieldEdit('location', e.target.value)}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleFieldSave('location')}>
                            <Check className="w-4 h-4 mr-1" />
                            {t('import_resume.save')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleFieldCancel}>
                            {t('import_resume.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-muted/20 rounded border">
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {parsedData.location}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingField('location')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('import_resume.phone')}</label>
                    {editingField === 'phone' ? (
                      <div className="space-y-2">
                        <Input
                          value={tempEditData.phone !== undefined ? tempEditData.phone : parsedData.phone}
                          onChange={(e) => handleFieldEdit('phone', e.target.value)}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleFieldSave('phone')}>
                            <Check className="w-4 h-4 mr-1" />
                            {t('import_resume.save')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleFieldCancel}>
                            {t('import_resume.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-muted/20 rounded border">
                        <span className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {parsedData.phone}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingField('phone')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('import_resume.website')}</label>
                    {editingField === 'website' ? (
                      <div className="space-y-2">
                        <Input
                          value={tempEditData.website !== undefined ? tempEditData.website : parsedData.website}
                          onChange={(e) => handleFieldEdit('website', e.target.value)}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleFieldSave('website')}>
                            <Check className="w-4 h-4 mr-1" />
                            {t('import_resume.save')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleFieldCancel}>
                            {t('import_resume.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-muted/20 rounded border">
                        <span className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          {parsedData.website}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingField('website')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('import_resume.linkedin')}</label>
                    {editingField === 'linkedin_url' ? (
                      <div className="space-y-2">
                        <Input
                          value={tempEditData.linkedin_url !== undefined ? tempEditData.linkedin_url : parsedData.linkedin_url}
                          onChange={(e) => handleFieldEdit('linkedin_url', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, 'linkedin_url')}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleFieldSave('linkedin_url')}>
                            <Check className="w-4 h-4 mr-1" />
                            {t('import_resume.save')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleFieldCancel}>
                            {t('import_resume.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-muted/20 rounded border">
                        <span className="flex items-center gap-2">
                          <Linkedin className="w-4 h-4" />
                          {parsedData.linkedin_url || ""}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingField('linkedin_url')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validation Summary */}
            {Object.keys(validationErrors).length > 0 && (
              <EnhancedAlert variant="warning" className="mb-6">
                <div>
                  <h4 className="font-semibold mb-2">{t('import_resume.validation_errors_title')}</h4>
                  <div className="space-y-1 text-sm">
                    {validationErrors.educations && validationErrors.educations.length > 0 && (
                      <p>
                        • {validationErrors.educations.length} {t('import_resume.education_errors')}
                      </p>
                    )}
                    {validationErrors.experiences && validationErrors.experiences.length > 0 && (
                      <p>
                        • {validationErrors.experiences.length} {t('import_resume.experience_errors')}
                      </p>
                    )}
                  </div>
                </div>
              </EnhancedAlert>
            )}

            {/* Experience Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  {t('import_resume.experience')} ({parsedData.experiences.length})
                </CardTitle>
                {!editingSections.experiences && (
                  <Button variant="outline" size="sm" onClick={() => setEditingSections(prev => ({ ...prev, experiences: true }))}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    {t('import_resume.edit')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingSections.experiences ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button onClick={addExperience} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('import_resume.add_experience')}
                      </Button>
                      <div className="flex gap-3">
                        <Button variant="outline" size="sm" onClick={() => {
                          setTempArrayEditData(prev => {
                            const newData = { ...prev };
                            delete newData.experiences;
                            return newData;
                          });
                          setEditingSections(prev => ({ ...prev, experiences: false }));
                        }}>{t('import_resume.cancel')}</Button>
                        <Button size="sm" onClick={() => {
                          applyArrayEdits('experiences');
                          setEditingSections(prev => ({ ...prev, experiences: false }));
                        }}>
                          {t('import_resume.save')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {getDisplayItems('experiences').map((exp, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-sm">{t('import_resume.experience')} #{index + 1}</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeExperience(index)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder={t('import_resume.position_required')}
                                value={getTempArrayValue('experiences', index, 'position', exp.position)}
                                onChange={(e) => handleArrayItemEdit('experiences', index, 'position', e.target.value)}
                              />
                              <Input
                                placeholder={t('import_resume.company_required')}
                                value={getTempArrayValue('experiences', index, 'company', exp.company)}
                                onChange={(e) => handleArrayItemEdit('experiences', index, 'company', e.target.value)}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder={t('import_resume.location_placeholder')}
                                value={getTempArrayValue('experiences', index, 'location', exp.location || "")}
                                onChange={(e) => handleArrayItemEdit('experiences', index, 'location', e.target.value)}
                              />
                              <Select 
                                value={getTempArrayValue('experiences', index, 'employment_type', exp.employment_type || "")} 
                                onValueChange={(value) => handleArrayItemEdit('experiences', index, 'employment_type', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={t('import_resume.employment_type')} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Full-time">{t('employment_types.full_time')}</SelectItem>
                                  <SelectItem value="Part-time">{t('employment_types.part_time')}</SelectItem>
                                  <SelectItem value="Contract">{t('employment_types.contract')}</SelectItem>
                                  <SelectItem value="Freelance">{t('employment_types.freelance')}</SelectItem>
                                  <SelectItem value="Internship">{t('employment_types.internship')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                type="date"
                                placeholder={t('import_resume.start_date')}
                                value={getTempArrayValue('experiences', index, 'start_date', exp.start_date || "")}
                                onChange={(e) => handleArrayItemEdit('experiences', index, 'start_date', e.target.value)}
                              />
                              <Input
                                type="date"
                                placeholder={t('import_resume.end_date')}
                                value={getTempArrayValue('experiences', index, 'end_date', exp.end_date || "")}
                                onChange={(e) => handleArrayItemEdit('experiences', index, 'end_date', e.target.value)}
                                disabled={getTempArrayValue('experiences', index, 'is_current', exp.is_current)}
                              />
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={getTempArrayValue('experiences', index, 'is_current', exp.is_current)}
                                onCheckedChange={(checked) => handleArrayItemEdit('experiences', index, 'is_current', checked)}
                              />
                              <label className="text-sm">{t('import_resume.currently_working')}</label>
                            </div>
                            <Textarea
                              placeholder={t('import_resume.role_description')}
                              value={getTempArrayValue('experiences', index, 'description', exp.description || "")}
                              onChange={(e) => handleArrayItemEdit('experiences', index, 'description', e.target.value)}
                              rows={3}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {parsedData.experiences && parsedData.experiences.length > 0 ? (
                      <div className="space-y-6">
                        {parsedData.experiences.map((exp, index) => (
                          <div key={index} className={`${index > 0 ? 'border-t pt-6' : ''}`}>
                            <div className="flex">
                              <div className="w-12 h-12 bg-muted rounded mr-4 flex items-center justify-center">
                                <Building2 className="w-6 h-6 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground">{exp.position}</h3>
                                <p className="text-muted-foreground font-medium">{exp.company} • {exp.employment_type}</p>
                                <div className="flex items-center text-sm text-muted-foreground mt-1">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {exp.start_date} - {exp.is_current ? t('import_resume.present') : exp.end_date}
                                  {exp.location && (
                                    <>
                                      <span className="mx-2">•</span>
                                      <MapPin className="w-4 h-4 mr-1" />
                                      {exp.location}
                                    </>
                                  )}
                                </div>
                                {exp.description && (
                                  <p className="text-sm text-foreground mt-3 mb-3 leading-relaxed">{exp.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">{t('import_resume.experience_empty_title')}</p>
                        <p className="text-sm mt-1">{t('import_resume.experience_empty_description')}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Education Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  {t('import_resume.education')} ({(parsedData.educations || []).length})
                </CardTitle>
                {!editingSections.educations && (
                  <Button variant="outline" size="sm" onClick={() => setEditingSections(prev => ({ ...prev, educations: true }))}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    {t('import_resume.edit')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingSections.educations ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button onClick={addEducation} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('import_resume.add_education')}
                      </Button>
                      <div className="flex gap-3">
                        <Button variant="outline" size="sm" onClick={() => {
                          setTempArrayEditData(prev => {
                            const newData = { ...prev };
                            delete newData.educations;
                            return newData;
                          });
                          setEditingSections(prev => ({ ...prev, educations: false }));
                        }}>{t('import_resume.cancel')}</Button>
                        <Button size="sm" onClick={() => {
                          applyArrayEdits('educations');
                          setEditingSections(prev => ({ ...prev, educations: false }));
                        }}>
                          {t('import_resume.save')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {getDisplayItems('educations').map((edu, index) => {
                        const eduErrors = validationErrors.educations?.find(e => e.index === index);
                        const hasError = !!eduErrors;
                        
                        return (
                          <div key={index} className={`p-4 border rounded-lg ${hasError ? 'border-destructive bg-destructive/5' : 'bg-muted/20'}`}>
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-medium text-sm">
                                {t('import_resume.education')} #{index + 1}
                                {hasError && (
                                  <span className="ml-2 text-destructive text-xs">
                                    ({t('import_resume.missing_required_fields')})
                                  </span>
                                )}
                              </h4>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEducation(index)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            {hasError && (
                              <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
                                {t('import_resume.required_fields')}: {eduErrors.fields.join(', ')}
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <Input
                                  placeholder={t('education_form.degree_placeholder')}
                                  value={getTempArrayValue('educations', index, 'degree', edu.degree)}
                                  onChange={(e) => handleArrayItemEdit('educations', index, 'degree', e.target.value)}
                                  className={eduErrors?.fields.includes('degree') ? 'border-destructive' : ''}
                                />
                                <Input
                                  placeholder={t('education_form.institution_placeholder')}
                                  value={getTempArrayValue('educations', index, 'institution', edu.institution)}
                                  onChange={(e) => handleArrayItemEdit('educations', index, 'institution', e.target.value)}
                                  className={eduErrors?.fields.includes('institution') ? 'border-destructive' : ''}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder={t('education_form.field_placeholder')}
                                value={getTempArrayValue('educations', index, 'field_of_study', edu.field_of_study || "")}
                                onChange={(e) => handleArrayItemEdit('educations', index, 'field_of_study', e.target.value)}
                              />
                              <Input
                                placeholder={t('education_form.grade_placeholder')}
                                value={getTempArrayValue('educations', index, 'grade', edu.grade || "")}
                                onChange={(e) => handleArrayItemEdit('educations', index, 'grade', e.target.value)}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                type="date"
                                placeholder={t('education_form.start_date_label')}
                                value={getTempArrayValue('educations', index, 'start_date', edu.start_date || "")}
                                onChange={(e) => handleArrayItemEdit('educations', index, 'start_date', e.target.value)}
                              />
                              <Input
                                type="date"
                                placeholder={t('education_form.end_date_label')}
                                value={getTempArrayValue('educations', index, 'end_date', edu.end_date || "")}
                                onChange={(e) => handleArrayItemEdit('educations', index, 'end_date', e.target.value)}
                                disabled={getTempArrayValue('educations', index, 'is_current', edu.is_current)}
                              />
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={getTempArrayValue('educations', index, 'is_current', edu.is_current)}
                                onCheckedChange={(checked) => handleArrayItemEdit('educations', index, 'is_current', checked)}
                              />
                              <label className="text-sm">{t('education_form.currently_studying')}</label>
                            </div>
                            <Textarea
                              placeholder={t('education_form.activities_placeholder')}
                              value={getTempArrayValue('educations', index, 'activities', edu.activities || "")}
                              onChange={(e) => handleArrayItemEdit('educations', index, 'activities', e.target.value)}
                              rows={2}
                            />
                            <Textarea
                              placeholder={t('education_form.description_placeholder')}
                              value={getTempArrayValue('educations', index, 'description', edu.description || "")}
                              onChange={(e) => handleArrayItemEdit('educations', index, 'description', e.target.value)}
                              rows={2}
                            />
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    {parsedData.educations && parsedData.educations.length > 0 ? (
                      <div className="space-y-6">
                        {parsedData.educations.map((edu, index) => (
                          <div key={index} className={`${index > 0 ? 'border-t pt-6' : ''}`}>
                            <div className="flex">
                              <div className="w-12 h-12 bg-muted rounded mr-4 flex items-center justify-center">
                                <GraduationCap className="w-6 h-6 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground">{edu.degree}</h3>
                                <p className="text-muted-foreground font-medium">{edu.institution}</p>
                                <div className="flex items-center text-sm text-muted-foreground mt-1">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {edu.start_date} - {edu.is_current ? t('import_resume.present') : edu.end_date}
                                </div>
                                {edu.field_of_study && (
                                  <p className="text-sm text-muted-foreground mt-1">{edu.field_of_study}</p>
                                )}
                                {edu.grade && (
                                  <p className="text-sm text-muted-foreground mt-1">{t('education_form.grade_placeholder')}: {edu.grade}</p>
                                )}
                                {edu.activities && (
                                  <p className="text-sm text-foreground mt-2">
                                    <span className="font-medium">{t('education_form.activities_placeholder')}: </span>
                                    {edu.activities}
                                  </p>
                                )}
                                {edu.description && (
                                  <p className="text-sm text-foreground mt-2">{edu.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">{t('import_resume.education_empty_title')}</p>
                        <p className="text-sm mt-1">{t('import_resume.education_empty_description')}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Projects Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  {t('import_resume.projects')} ({parsedData.projects.length})
                </CardTitle>
                {!editingSections.projects && (
                  <Button variant="outline" size="sm" onClick={() => setEditingSections(prev => ({ ...prev, projects: true }))}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    {t('import_resume.edit')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingSections.projects ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button onClick={addProject} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('import_resume.add_project')}
                      </Button>
                      <div className="flex gap-3">
                        <Button variant="outline" size="sm" onClick={() => {
                          setTempArrayEditData(prev => {
                            const newData = { ...prev };
                            delete newData.projects;
                            return newData;
                          });
                          setEditingSections(prev => ({ ...prev, projects: false }));
                        }}>{t('import_resume.cancel')}</Button>
                        <Button size="sm" onClick={() => {
                          applyArrayEdits('projects');
                          setEditingSections(prev => ({ ...prev, projects: false }));
                        }}>
                          {t('import_resume.save')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {getDisplayItems('projects').map((project, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-sm">{t('import_resume.project')} #{index + 1}</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeProject(index)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-3">
                            <Input
                              placeholder={t('project_form.title_placeholder')}
                              value={getTempArrayValue('projects', index, 'title', project.title)}
                              onChange={(e) => handleArrayItemEdit('projects', index, 'title', e.target.value)}
                            />
                            <Textarea
                              placeholder={t('project_form.description_placeholder')}
                              value={getTempArrayValue('projects', index, 'description', project.description)}
                              onChange={(e) => handleArrayItemEdit('projects', index, 'description', e.target.value)}
                              rows={3}
                            />
                            <Input
                              placeholder={t('project_form.url_placeholder')}
                              value={getTempArrayValue('projects', index, 'url', project.url || "")}
                              onChange={(e) => handleArrayItemEdit('projects', index, 'url', e.target.value)}
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                type="date"
                                placeholder={t('project_form.start_date_placeholder')}
                                value={getTempArrayValue('projects', index, 'startDate', project.startDate || "")}
                                onChange={(e) => handleArrayItemEdit('projects', index, 'startDate', e.target.value)}
                              />
                              <Input
                                type="date"
                                placeholder={t('project_form.end_date_placeholder')}
                                value={getTempArrayValue('projects', index, 'endDate', project.endDate || "")}
                                onChange={(e) => handleArrayItemEdit('projects', index, 'endDate', e.target.value)}
                              />
                            </div>
                            <Input
                              placeholder={t('project_form.technologies_placeholder')}
                              value={Array.isArray(getTempArrayValue('projects', index, 'technologies', project.technologies)) ? getTempArrayValue('projects', index, 'technologies', project.technologies).join(', ') : ''}
                              onChange={(e) => handleArrayItemEdit('projects', index, 'technologies', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {parsedData.projects && parsedData.projects.length > 0 ? (
                      <div className="space-y-6">
                        {parsedData.projects.map((project, index) => (
                          <div key={index} className={`${index > 0 ? 'border-t pt-6' : ''}`}>
                            <div className="flex">
                              <div className="w-12 h-12 bg-muted rounded mr-4 flex items-center justify-center">
                                <FolderOpen className="w-6 h-6 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground">{project.title}</h3>
                                {project.description && (
                                  <p className="text-sm text-foreground mt-2 leading-relaxed">{project.description}</p>
                                )}
                                {project.technologies && project.technologies.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-3">
                                    {project.technologies.map((tech: string, techIndex: number) => (
                                      <Badge key={techIndex} variant="secondary" className="text-xs">
                                        {tech}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-3">
                                  {project.startDate && (
                                    <div className="flex items-center">
                                      <Calendar className="w-4 h-4 mr-1" />
                                      {project.startDate} - {project.endDate ? project.endDate : t('import_resume.present')}
                                    </div>
                                  )}
                                  {project.url && (
                                    <a href={project.url} target="_blank" rel="noopener noreferrer" 
                                       className="flex items-center hover:text-primary transition-colors">
                                      <Globe className="w-4 h-4 mr-1" />
                                      {t('import_resume.view_project')}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">{t('import_resume.project_empty_title')}</p>
                        <p className="text-sm mt-1">{t('import_resume.project_empty_description')}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Certifications Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t('import_resume.certifications')} ({parsedData.certifications.length})
                </CardTitle>
                {!editingSections.certifications && (
                  <Button variant="outline" size="sm" onClick={() => setEditingSections(prev => ({ ...prev, certifications: true }))}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    {t('import_resume.edit')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingSections.certifications ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button onClick={addCertification} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('import_resume.add_certification')}
                      </Button>
                      <div className="flex gap-3">
                        <Button variant="outline" size="sm" onClick={() => {
                          setTempArrayEditData(prev => {
                            const newData = { ...prev };
                            delete newData.certifications;
                            return newData;
                          });
                          setEditingSections(prev => ({ ...prev, certifications: false }));
                        }}>{t('import_resume.cancel')}</Button>
                        <Button size="sm" onClick={() => {
                          applyArrayEdits('certifications');
                          setEditingSections(prev => ({ ...prev, certifications: false }));
                        }}>
                          {t('import_resume.save')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {getDisplayItems('certifications').map((cert, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-sm">{t('import_resume.certification')} #{index + 1}</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCertification(index)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-3">
                            <Input
                              placeholder={t('certification_form.name_placeholder')}
                              value={getTempArrayValue('certifications', index, 'name', cert.name)}
                              onChange={(e) => handleArrayItemEdit('certifications', index, 'name', e.target.value)}
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder={t('certification_form.issuer_placeholder')}
                                value={getTempArrayValue('certifications', index, 'issuer', cert.issuer || "")}
                                onChange={(e) => handleArrayItemEdit('certifications', index, 'issuer', e.target.value)}
                              />
                              <Input
                                type="date"
                                placeholder={t('certification_form.date_placeholder')}
                                value={getTempArrayValue('certifications', index, 'date', cert.date || "")}
                                onChange={(e) => handleArrayItemEdit('certifications', index, 'date', e.target.value)}
                              />
                            </div>
                            <Input
                              placeholder={t('certification_form.url_placeholder')}
                              value={getTempArrayValue('certifications', index, 'url', cert.url || "")}
                              onChange={(e) => handleArrayItemEdit('certifications', index, 'url', e.target.value)}
                            />
                            <Textarea
                              placeholder={t('certification_form.description_placeholder')}
                              value={getTempArrayValue('certifications', index, 'description', cert.description || "")}
                              onChange={(e) => handleArrayItemEdit('certifications', index, 'description', e.target.value)}
                              rows={2}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {parsedData.certifications && parsedData.certifications.length > 0 ? (
                      <div className="space-y-6">
                        {parsedData.certifications.map((cert, index) => (
                          <div key={index} className={`${index > 0 ? 'border-t pt-6' : ''}`}>
                            <div className="flex">
                              <div className="w-12 h-12 bg-muted rounded mr-4 flex items-center justify-center">
                                <Shield className="w-6 h-6 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground">{cert.name}</h3>
                                {cert.issuer && (
                                  <p className="text-muted-foreground font-medium">{cert.issuer}</p>
                                )}
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                                  {cert.date && (
                                    <div className="flex items-center">
                                      <Calendar className="w-4 h-4 mr-1" />
                                      {cert.date}
                                    </div>
                                  )}
                                  {cert.url && (
                                    <a href={cert.url} target="_blank" rel="noopener noreferrer" 
                                       className="flex items-center hover:text-primary transition-colors">
                                      <Globe className="w-4 h-4 mr-1" />
                                      {t('import_resume.view_credential')}
                                    </a>
                                  )}
                                </div>
                                {cert.description && (
                                  <p className="text-sm text-foreground mt-2 leading-relaxed">{cert.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">{t('import_resume.certification_empty_title')}</p>
                        <p className="text-sm mt-1">{t('import_resume.certification_empty_description')}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Awards Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AwardIcon className="w-5 h-5" />
                  {t('import_resume.awards')} ({parsedData.awards.length})
                </CardTitle>
                {!editingSections.awards && (
                  <Button variant="outline" size="sm" onClick={() => setEditingSections(prev => ({ ...prev, awards: true }))}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    {t('import_resume.edit')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingSections.awards ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Button onClick={addAward} variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('import_resume.add_award')}
                      </Button>
                      <div className="flex gap-3">
                        <Button variant="outline" size="sm" onClick={() => {
                          setTempArrayEditData(prev => {
                            const newData = { ...prev };
                            delete newData.awards;
                            return newData;
                          });
                          setEditingSections(prev => ({ ...prev, awards: false }));
                        }}>{t('import_resume.cancel')}</Button>
                        <Button size="sm" onClick={() => {
                          applyArrayEdits('awards');
                          setEditingSections(prev => ({ ...prev, awards: false }));
                        }}>
                          {t('import_resume.save')}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {getDisplayItems('awards').map((award, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-sm">{t('import_resume.award')} #{index + 1}</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAward(index)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-3">
                            <Input
                              placeholder={t('award_form.title_placeholder')}
                              value={getTempArrayValue('awards', index, 'title', award.title)}
                              onChange={(e) => handleArrayItemEdit('awards', index, 'title', e.target.value)}
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <Input
                                placeholder={t('award_form.issuer_placeholder')}
                                value={getTempArrayValue('awards', index, 'issuer', award.issuer || "")}
                                onChange={(e) => handleArrayItemEdit('awards', index, 'issuer', e.target.value)}
                              />
                              <Input
                                type="date"
                                placeholder={t('award_form.date_placeholder')}
                                value={getTempArrayValue('awards', index, 'date', award.date || "")}
                                onChange={(e) => handleArrayItemEdit('awards', index, 'date', e.target.value)}
                              />
                            </div>
                            <Textarea
                              placeholder={t('award_form.description_placeholder')}
                              value={getTempArrayValue('awards', index, 'description', award.description || "")}
                              onChange={(e) => handleArrayItemEdit('awards', index, 'description', e.target.value)}
                              rows={3}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {parsedData.awards && parsedData.awards.length > 0 ? (
                      <div className="space-y-6">
                        {parsedData.awards.map((award, index) => (
                          <div key={index} className={`${index > 0 ? 'border-t pt-6' : ''}`}>
                            <div className="flex">
                              <div className="w-12 h-12 bg-muted rounded mr-4 flex items-center justify-center">
                                <AwardIcon className="w-6 h-6 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground">{award.title}</h3>
                                {award.issuer && (
                                  <p className="text-muted-foreground font-medium">{award.issuer}</p>
                                )}
                                {award.date && (
                                  <div className="flex items-center text-sm text-muted-foreground mt-1">
                                    <Calendar className="w-4 h-4 mr-1" />
                                    {award.date}
                                  </div>
                                )}
                                {award.description && (
                                  <p className="text-sm text-foreground mt-2 leading-relaxed">{award.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <AwardIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">{t('import_resume.award_empty_title')}</p>
                         <p className="text-sm mt-1">{t('import_resume.award_empty_description')}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between sticky bottom-0 bg-background py-4 border-t">
              <Button variant="outline" onClick={handleBackToUpload}>
                {t('import_resume.back_to_upload')}
              </Button>
              <Button 
                onClick={handleImportToProfile} 
                disabled={isImporting || Object.keys(validationErrors).length > 0}
              >
                {isImporting ? t('import_resume.importing') : t('import_resume.import_to_profile')}
              </Button>
            </div>
          </div>
        </ContentComponent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('import_resume.title')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <BodySmall className="text-muted-foreground">
            {t('import_resume.description')}
          </BodySmall>
          
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <BodySmall className="text-destructive">{error}</BodySmall>
            </div>
          )}

          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <BodySmall>
                  {step === 'upload' && t('import_resume.uploading_file')}
                </BodySmall>
                <Caption>{progress}%</Caption>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
          
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? "border-primary bg-primary/5" 
                : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <InlineLoader 
                  size="xl" 
                  text={
                    step === 'upload' ? t('import_resume.uploading_file') :
                    t('import_resume.analysis_complete')
                  }
                />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <Upload className="w-12 h-12 text-muted-foreground" />
                <div>
                  <BodyMedium className="font-medium">{t('import_resume.drop_zone_title')}</BodyMedium>
                  <Caption className="text-muted-foreground mt-1">
                    {t('import_resume.drop_zone_subtitle')}
                  </Caption>
                </div>
                <label htmlFor="resume-upload" className="cursor-pointer">
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span>{t('import_resume.browse_files')}</span>
                  </Button>
                  <Input
                    id="resume-upload"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleInputChange}
                    className="sr-only"
                    disabled={uploading}
                  />
                </label>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={uploading}>
              {t('import_resume.cancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { ImportResumeDialog };
